import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/components/Layout/Layout"
import { ReadMore } from "@jobteaser/ui-kit/components/ReadMore/ReadMore"
import { RichText } from "@jobteaser/ui-kit/components/RichText/RichText"

import { isVideoUrlValid } from "@/modules/companies/client/components/Media/utils/isVideoUrlValid"
import { MEDIA_TYPE_IMAGE } from "@/modules/companies/client/constants/enums"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useTracking } from "@/modules/tracking/useTracking"

import styles from "./What.module.css"

const VideoDynamic = dynamic(() =>
  import("@/modules/companies/client/components/Media/Video").then(mod => mod.MediaVideo)
)

const ImageDynamic = dynamic(() =>
  import("@/modules/companies/client/components/Media/Image").then(mod => mod.MediaImage)
)

const PRESENTATION_NB_LIMIT_LINE = 10

export const What: FunctionComponent = () => {
  const tracking = useTracking()
  const { dataCompany } = useCompanyFetch()
  const { t } = useTranslation(["companies_client"])

  if (!dataCompany) return null

  const {
    id,
    page: { name, presentation, institutionalVideo, gallery },
  } = dataCompany

  const isDisplayedVideo = isVideoUrlValid(institutionalVideo.url)

  const firstGalleryImage = gallery.filter(media => media.type === MEDIA_TYPE_IMAGE)[0] || {}
  const firstGalleryImageUrl = firstGalleryImage?.url
  const firstGalleryImageDescription = firstGalleryImage?.description || ""
  const isGalleryImage = !isDisplayedVideo && !!firstGalleryImageUrl

  const hasMedia = isDisplayedVideo || isGalleryImage

  if (!hasMedia && !presentation) return null
  return (
    <Layout hasBottomPadding colorBlock={LAYOUT_COLOR_BLOCK.WHITE} data-testid="company_information_what">
      <Layout.Column>
        <Heading
          className={styles.title}
          data-testid="company_information_what_title"
          as="h2"
          variant="title2"
          weight="bold"
        >
          {t("companies_client.information.what.title", { name })}
        </Heading>
        <div className={styles.container}>
          {hasMedia && (
            <div data-testid="company_information_illustration">
              {isDisplayedVideo && (
                <VideoDynamic
                  video={institutionalVideo}
                  className={styles.mediaVideo}
                  withDescription={false}
                  priority
                  tracking={{ id, name }}
                />
              )}
              {isGalleryImage && (
                <ImageDynamic
                  className={styles.mediaImage}
                  src={firstGalleryImageUrl}
                  alt={firstGalleryImageDescription}
                  priority
                  data-testid="company_information_what_image"
                />
              )}
            </div>
          )}
          {presentation && (
            <div className={styles.presentation}>
              <ReadMore
                lineLimit={PRESENTATION_NB_LIMIT_LINE}
                onClick={() =>
                  tracking?.track("see_more_information_click", {
                    company_id: id,
                    company_name: name,
                    context: "presentation",
                    service: "company",
                  })
                }
                readMoreText={t("companies_client.presentation.readMore")}
              >
                <RichText
                  className={styles.richText}
                  data-testid="company_information_what_content"
                  allowTargetAttribute
                >
                  {presentation}
                </RichText>
              </ReadMore>
            </div>
          )}
        </div>
      </Layout.Column>
    </Layout>
  )
}
